import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useSocioViewAsEmail } from "@/components/admin/portal-socios/SocioImpersonationContext";
import { arr, callSocioRpc, num, numOrNull, strOrNull } from "./_rpc";
import type {
  RpcState,
  SocioActualizacion,
  SocioDashboard,
  SocioListadoRow,
  SocioProyecto,
  SocioRepoArchivo,
  SocioRepoCategoria,
  TipoArchivoRepo,
} from "./types";

const STALE = 60_000;

/* ------------------------------------------------------------------ */
/* socio_mis_proyectos(p_email_socio)                                  */
/* ------------------------------------------------------------------ */
/**
 * Proyectos del usuario en sesión o, con `emailSocio` (sólo Super Admin, "Ver como"),
 * los de ese socio. Sin email se llama sin parámetros para seguir funcionando con
 * la versión anterior de la RPC.
 */
export function useSocioMisProyectos(emailSocio: string | null = null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["portal-socios", "mis-proyectos", user?.id ?? null, emailSocio],
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: (): Promise<RpcState<SocioProyecto[]>> =>
      callSocioRpc(
        "socio_mis_proyectos",
        emailSocio ? { p_email_socio: emailSocio } : undefined,
        (raw) =>
          arr(raw).map((r) => ({
            id_proyecto: num(r.id_proyecto),
            nombre: String(r.nombre ?? `Proyecto #${r.id_proyecto}`),
            logo_url: strOrNull(r.logo_url),
            unidades_asignadas: num(r.unidades_asignadas),
            etapa: strOrNull(r.etapa),
            porcentaje_avance: numOrNull(r.porcentaje_avance),
          })),
        [],
      ),
  });
}

/* ------------------------------------------------------------------ */
/* get_socio_dashboard(p_id_proyecto, p_meses)                         */
/* ------------------------------------------------------------------ */
function normalizeDashboard(raw: any): SocioDashboard {
  const p = raw?.proyecto ?? {};
  const k = raw?.kpis ?? {};
  const inv = raw?.inventario_actual ?? {};
  return {
    proyecto: {
      id: num(p.id),
      nombre: String(p.nombre ?? ""),
      logo_url: strOrNull(p.logo_url),
      portada_url: strOrNull(p.portada_url),
      total_unidades: num(p.total_unidades),
      ultima_actualizacion: strOrNull(p.ultima_actualizacion),
    },
    kpis: {
      avance_pct: numOrNull(k.avance_pct),
      avance_etapa: strOrNull(k.avance_etapa),
      avance_pct_anterior: numOrNull(k.avance_pct_anterior),
      avance_fecha_corte: strOrNull(k.avance_fecha_corte),
      desviacion_dias: numOrNull(k.desviacion_dias),
      estado_plan: (k.estado_plan ?? null) as SocioDashboard["kpis"]["estado_plan"],
      unidades_total: num(k.unidades_total),
      unidades_vendidas: num(k.unidades_vendidas),
      unidades_disponibles: num(k.unidades_disponibles),
      unidades_asignadas: num(k.unidades_asignadas),
      unidades_en_cierre: num(k.unidades_en_cierre),
      precio_m2_disponible: numOrNull(k.precio_m2_disponible),
      precio_m2_vendido: numOrNull(k.precio_m2_vendido),
      precio_m2_vendido_mes_anterior: numOrNull(k.precio_m2_vendido_mes_anterior),
      booking_mes_unidades: num(k.booking_mes_unidades),
      booking_mes_monto: num(k.booking_mes_monto),
      ofertas_mes: num(k.ofertas_mes),
      citas_mes: num(k.citas_mes),
      citas_agendadas_mes: numOrNull(k.citas_agendadas_mes),
    },
    mensual: arr(raw?.mensual).map((m) => ({
      periodo: String(m.periodo),
      ventas_unidades: num(m.ventas_unidades),
      ventas_monto: num(m.ventas_monto),
      precio_m2_vendido: numOrNull(m.precio_m2_vendido),
      ofertas: num(m.ofertas),
      citas_atendidas: num(m.citas_atendidas),
      citas_agendadas: numOrNull(m.citas_agendadas),
      booking_unidades: numOrNull(m.booking_unidades),
      booking_monto: numOrNull(m.booking_monto),
      asignadas_unidades: numOrNull(m.asignadas_unidades),
      disponibles_unidades: numOrNull(m.disponibles_unidades),
      asignadas_monto: numOrNull(m.asignadas_monto),
      disponibles_monto: numOrNull(m.disponibles_monto),
    })),
    inventario_actual: {
      vendidas: num(inv.vendidas),
      asignadas: num(inv.asignadas),
      disponibles: num(inv.disponibles),
      otras: num(inv.otras),
    },
    inventario_por_tipo: arr(raw?.inventario_por_tipo)
      .map((t) => ({
        segmento: (String(t.segmento ?? "").toLowerCase() === "comercial" ? "comercial" : "residencial") as
          | "residencial"
          | "comercial",
        tipo: String(t.tipo ?? (t.segmento === "comercial" ? "Comercial" : "Departamentos")),
        total: num(t.total),
        vendidas: num(t.vendidas),
        asignadas: num(t.asignadas),
        disponibles: num(t.disponibles),
        en_cierre: num(t.en_cierre),
      }))
      .filter((t) => t.total > 0),
    avance_serie: arr(raw?.avance_serie).map((a) => ({
      periodo: String(a.periodo),
      porcentaje: num(a.porcentaje),
    })),
    hitos: arr(raw?.hitos)
      .map((h) => ({
        id: num(h.id),
        nombre: String(h.nombre ?? ""),
        orden: num(h.orden, 100),
        fase: strOrNull(h.fase),
        tipo: strOrNull(h.tipo),
        es_critico: h.es_critico === true,
        pct_acumulado: numOrNull(h.pct_acumulado),
        fecha_plan_inicio: strOrNull(h.fecha_plan_inicio),
        fecha_plan_fin: strOrNull(h.fecha_plan_fin),
        fecha_real_inicio: strOrNull(h.fecha_real_inicio),
        fecha_real_fin: strOrNull(h.fecha_real_fin),
        fecha_proyectada_fin: strOrNull(h.fecha_proyectada_fin),
        estatus: strOrNull(h.estatus),
        desviacion_dias: numOrNull(h.desviacion_dias),
      }))
      .sort((a, b) => a.orden - b.orden),
    ...normalizeMultimedia(raw),
    actualizaciones: arr(raw?.actualizaciones).map((a) => ({
      id: num(a.id),
      fecha: String(a.fecha ?? ""),
      titulo: String(a.titulo ?? ""),
      categoria: String(a.categoria ?? "general"),
      resumen: strOrNull(a.resumen),
      portada_url: strOrNull(a.portada_url),
    })),
  };
}

/**
 * Shape nuevo: `galeria` (sólo «Avances de obra») + `videos` (YouTube).
 * Shape viejo: todo en `galeria`, distinguido por `origen: 'youtube'`.
 */
function normalizeMultimedia(raw: any): Pick<SocioDashboard, "galeria" | "videos"> {
  const items = arr(raw?.galeria);
  const esYoutube = (g: Record<string, unknown>) => String(g.origen ?? "").toLowerCase() === "youtube";
  const video = (v: Record<string, unknown>) => ({
    id: num(v.id),
    url: String(v.url ?? ""),
    titulo: strOrNull(v.titulo),
    fecha: strOrNull(v.fecha),
  });
  const galeria = items
    .filter((g) => !esYoutube(g))
    .map((g) => ({
      id: num(g.id),
      tipo: (g.tipo === "video" ? "video" : "foto") as "foto" | "video",
      url: String(g.url ?? ""),
      titulo: strOrNull(g.titulo),
      fecha: strOrNull(g.fecha),
    }))
    .filter((g) => g.url);
  const videos = (Array.isArray(raw?.videos) ? arr(raw.videos) : items.filter(esYoutube)).map(video).filter((v) => v.url);
  return { galeria, videos };
}

export function useSocioDashboard(idProyecto: number | null, meses = 12) {
  const viewAs = useSocioViewAsEmail();
  return useQuery({
    queryKey: ["portal-socios", "dashboard", idProyecto, meses, viewAs],
    enabled: idProyecto != null,
    staleTime: STALE,
    placeholderData: (prev) => prev,
    queryFn: (): Promise<RpcState<SocioDashboard | null>> =>
      callSocioRpc(
        "get_socio_dashboard",
        { p_id_proyecto: idProyecto, p_meses: meses },
        (raw) => (raw ? normalizeDashboard(raw) : null),
        null,
      ),
  });
}

/* ------------------------------------------------------------------ */
/* get_socio_actualizaciones(p_id_proyecto, p_categoria, p_search)     */
/* ------------------------------------------------------------------ */
export function useSocioActualizaciones(
  idProyecto: number | null,
  categoria: string | null,
  search: string | null,
) {
  const viewAs = useSocioViewAsEmail();
  return useQuery({
    queryKey: ["portal-socios", "actualizaciones", idProyecto, categoria, search, viewAs],
    enabled: idProyecto != null,
    staleTime: STALE,
    placeholderData: (prev) => prev,
    queryFn: (): Promise<RpcState<SocioActualizacion[]>> =>
      callSocioRpc(
        "get_socio_actualizaciones",
        {
          p_id_proyecto: idProyecto,
          p_categoria: categoria || null,
          p_search: search?.trim() || null,
        },
        (raw) => {
          const list = Array.isArray(raw) ? raw : arr((raw as any)?.actualizaciones);
          return list.map((a: any) => ({
            id: num(a.id),
            fecha: String(a.fecha ?? ""),
            titulo: String(a.titulo ?? ""),
            categoria: String(a.categoria ?? "general"),
            estatus: strOrNull(a.estatus),
            contexto: strOrNull(a.contexto ?? a.resumen),
            portada_url: strOrNull(a.portada_url),
            adjuntos: arr(a.adjuntos).map((j: any) => ({
              id: num(j.id),
              url: String(j.url ?? ""),
              nombre: strOrNull(j.nombre),
              tipo: String(j.tipo ?? "documento"),
            })),
          }));
        },
        [],
      ),
  });
}

/* ------------------------------------------------------------------ */
/* get_socio_repositorio(p_id_proyecto)                                */
/* ------------------------------------------------------------------ */
export function inferTipoArchivo(url: string, hint?: string | null): TipoArchivoRepo {
  const h = (hint ?? "").toLowerCase();
  if (["imagen", "video", "pdf", "documento", "360", "enlace"].includes(h)) return h as TipoArchivoRepo;
  if (h === "foto") return "imagen";
  const u = url.toLowerCase().split("?")[0];
  if (/youtube\.com|youtu\.be|vimeo\.com|\.mp4$|\.mov$|\.webm$/.test(url.toLowerCase())) return "video";
  if (/matterport|kuula|360|panorama|tour/.test(url.toLowerCase())) return "360";
  if (/\.(png|jpe?g|webp|gif|avif|svg)$/.test(u)) return "imagen";
  if (/\.pdf$/.test(u)) return "pdf";
  if (/\.(docx?|xlsx?|pptx?|zip|dwg|csv|txt)$/.test(u)) return "documento";
  return "enlace";
}

export function tituloDesdeUrl(url: string): string {
  try {
    const last = decodeURIComponent(url.split("?")[0].split("/").pop() || "");
    return last.replace(/^\d{10,}_[a-z0-9]+\./i, "archivo.") || "Archivo";
  } catch {
    return "Archivo";
  }
}

export function useSocioRepositorio(idProyecto: number | null) {
  const viewAs = useSocioViewAsEmail();
  return useQuery({
    queryKey: ["portal-socios", "repositorio", idProyecto, viewAs],
    enabled: idProyecto != null,
    staleTime: STALE,
    queryFn: (): Promise<RpcState<SocioRepoCategoria[]>> =>
      callSocioRpc(
        "get_socio_repositorio",
        { p_id_proyecto: idProyecto },
        (raw) => {
          const cats = Array.isArray(raw) ? raw : arr((raw as any)?.categorias);
          // Los videos (videos_youtube) llegan aparte: se muestran como una categoría más.
          const videos = Array.isArray(raw) ? [] : arr((raw as any)?.videos);
          const conVideos = videos.length
            ? [...cats, { clave: "videos", nombre: "Videos", archivos: videos.map((v: any) => ({ ...v, tipo: "video" })) }]
            : cats;
          return conVideos.map((c: any, i: number) => ({
            id: c.clave ?? c.id_categoria ?? c.id ?? `cat-${i}`,
            nombre: String(c.nombre ?? "Sin categoría"),
            archivos: arr(c.archivos).map(
              (f: any, j: number): SocioRepoArchivo => {
                const url = String(f.url ?? f.link ?? "");
                return {
                  id: String(f.id ?? `${i}-${j}`),
                  url,
                  titulo: String(f.titulo ?? f.nombre ?? tituloDesdeUrl(url)),
                  tipo: inferTipoArchivo(url, f.tipo ?? (f.es_imagen === true ? "imagen" : undefined)),
                  fecha: strOrNull(f.fecha ?? f.fecha_creacion),
                };
              },
            ),
          }));
        },
        [],
      ),
  });
}

/* ------------------------------------------------------------------ */
/* get_socios_listado()                                                */
/* ------------------------------------------------------------------ */
export function useSociosListado(enabled = true) {
  return useQuery({
    queryKey: ["portal-socios", "listado"],
    enabled,
    staleTime: STALE,
    queryFn: (): Promise<RpcState<SocioListadoRow[]>> =>
      callSocioRpc(
        "get_socios_listado",
        undefined,
        (raw) =>
          arr(raw).map((r) => ({
            id_persona: num(r.id_persona),
            nombre: String(r.nombre ?? ""),
            email: strOrNull(r.email),
            email_usuario: strOrNull(r.email_usuario),
            rol_principal: strOrNull(r.rol_principal),
            proyectos: arr(r.proyectos).map(String),
            unidades: num(r.unidades),
            rol_socio_activo: !!r.rol_socio_activo,
          })),
        [],
      ),
  });
}

/* ------------------------------------------------------------------ */
/* get_socios_personas() — para badges                                 */
/* ------------------------------------------------------------------ */
export interface SociosPersonasSet {
  personaIds: Set<number>;
  emails: Set<string>;
  isSocioPersona: (idPersona: number | null | undefined) => boolean;
  isSocioEmail: (email: string | null | undefined) => boolean;
}

export function useSociosPersonas(): SociosPersonasSet {
  const q = useQuery({
    queryKey: ["portal-socios", "personas-socias"],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const r = await callSocioRpc(
          "get_socios_personas",
          undefined,
          (raw) => arr(raw).map((x) => ({ id: num(x.id_persona), email: strOrNull(x.email) })),
          [] as Array<{ id: number; email: string | null }>,
        );
        return r.data;
      } catch {
        // El badge es decorativo: un error nunca debe romper la pantalla que lo usa.
        return [];
      }
    },
  });
  const rows = q.data;
  return useMemo(() => {
    const list = rows ?? [];
    const personaIds = new Set(list.map((r) => r.id));
    const emails = new Set(list.map((r) => (r.email ?? "").trim().toLowerCase()).filter(Boolean));
    return {
      personaIds,
      emails,
      isSocioPersona: (id: number | null | undefined) => id != null && personaIds.has(Number(id)),
      isSocioEmail: (e: string | null | undefined) => !!e && emails.has(e.trim().toLowerCase()),
    };
  }, [rows]);
}
